import React from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useMediaQuery,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  ListItemText,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SavingsIcon from '@mui/icons-material/Savings';
import LinearProgress from '@mui/material/LinearProgress';
import PaymentsIcon from '@mui/icons-material/Payments';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BuildIcon from '@mui/icons-material/Build';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const TIPOS_DESPESA = ['Manutenção', 'Obras', 'Projetos', 'Reformas'];
const TIPO_COLORS = {
  Manutenção: '#7b1fa2',
  Obras: '#0277bd',
  Projetos: '#00695c',
  Reformas: '#e65100',
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function getBudgetColor(pct) {
  if (pct >= 90) return '#d32f2f';
  if (pct >= 75) return '#ff9800';
  return '#4caf50';
}

function calcBudgetPct(c) {
  const consumed =
    (c.despesas?.total || 0) + (c.contratos?.total || 0) + (c.estoque?.total_valor || 0);
  const budget = c.budget || 0;
  return budget > 0 ? (consumed / budget) * 100 : 0;
}

function clampBudgetPct(pct) {
  return Math.max(0, Math.min(pct, 100));
}

function calcBudgetConsumed(c) {
  return (c.despesas?.total || 0) + (c.contratos?.total || 0) + (c.estoque?.total_valor || 0);
}

export default function ResumoMensal() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { token } = useAuth();
  const [tabIndex, setTabIndex] = React.useState(0);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [expandedRows, setExpandedRows] = React.useState({});
  const [expandedCells, setExpandedCells] = React.useState({});
  const [expandedChart, setExpandedChart] = React.useState(null);

  // Filter states
  const [filtroCliente, setFiltroCliente] = React.useState('');
  const [filtroSituacao, setFiltroSituacao] = React.useState('');
  const [filtroFornecedor, setFiltroFornecedor] = React.useState('');
  const [filtroDataInicio, setFiltroDataInicio] = React.useState('');
  const [filtroDataFim, setFiltroDataFim] = React.useState('');
  const [filtroTipos, setFiltroTipos] = React.useState([]);
  const [filtroNumeroOS, setFiltroNumeroOS] = React.useState('');
  const [inputNumeroOS, setInputNumeroOS] = React.useState('');
  const [clientesList, setClientesList] = React.useState([]);
  const [fornecedoresList, setFornecedoresList] = React.useState([]);

  const API = '/api';
  const headers = React.useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Debounce 3s no campo Nº OS
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFiltroNumeroOS(inputNumeroOS.trim());
    }, 3000);
    return () => clearTimeout(timer);
  }, [inputNumeroOS]);

  // Load filter options once
  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const [cResp, fResp] = await Promise.all([
          fetch(`${API}/clientes`, { headers }),
          fetch(`${API}/fornecedores`, { headers }),
        ]);
        if (cResp.ok) {
          const cJson = await cResp.json();
          setClientesList(Array.isArray(cJson) ? cJson : cJson.data || []);
        }
        if (fResp.ok) {
          const fJson = await fResp.json();
          setFornecedoresList(Array.isArray(fJson) ? fJson : fJson.data || []);
        }
      } catch (e) {
        console.error('Erro ao carregar filtros', e);
      }
    };
    loadOptions();
  }, [headers]);

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filtroCliente) params.set('cliente_id', filtroCliente);
      if (filtroSituacao) params.set('situacao', filtroSituacao);
      if (filtroFornecedor) params.set('fornecedor_id', filtroFornecedor);
      if (filtroTipos.length > 0) params.set('tipo', filtroTipos.join(','));
      if (filtroDataInicio) params.set('data_inicio', filtroDataInicio);
      if (filtroDataFim) params.set('data_fim', filtroDataFim);
      if (filtroNumeroOS) params.set('numero_os', filtroNumeroOS.trim());
      const qs = params.toString();
      const resp = await fetch(`${API}/resumo_mensals/dashboard${qs ? '?' + qs : ''}`, { headers });
      if (!resp.ok) throw new Error('Erro ao carregar dados');
      const json = await resp.json();
      setData(json);
    } catch (e) {
      console.error(e);
      setError('Erro ao carregar dados do resumo mensal.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    API,
    headers,
    filtroCliente,
    filtroSituacao,
    filtroFornecedor,
    filtroTipos,
    filtroDataInicio,
    filtroDataFim,
    filtroNumeroOS,
  ]);

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleClearFilters = () => {
    setFiltroCliente('');
    setFiltroSituacao('');
    setFiltroFornecedor('');
    setFiltroTipos([]);
    setFiltroNumeroOS('');
    setInputNumeroOS('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
  };

  const hasActiveFilters =
    filtroCliente ||
    filtroSituacao ||
    filtroFornecedor ||
    filtroTipos.length > 0 ||
    inputNumeroOS ||
    filtroDataInicio ||
    filtroDataFim;

  const tiposVisiveis = React.useMemo(() => {
    const selecionados = filtroTipos.filter((t) => t !== '_vazio');
    return selecionados.length > 0 ? selecionados : TIPOS_DESPESA;
  }, [filtroTipos]);

  const toggleRow = (clienteId) => {
    setExpandedRows((prev) => ({ ...prev, [clienteId]: !prev[clienteId] }));
  };

  const toggleCell = (clienteId, col) => {
    setExpandedCells((prev) => {
      const cur = prev[clienteId];
      if (cur?.open && cur?.col === col) return { ...prev, [clienteId]: { col, open: false } };
      return { ...prev, [clienteId]: { col, open: true } };
    });
  };
  const isCellOpen = (clienteId, col) =>
    !!(expandedCells[clienteId]?.open && expandedCells[clienteId]?.col === col);

  const handleExportCSV = () => {
    if (!data?.clientes) return;
    const sep = ';';
    const tipoHeaders = TIPOS_DESPESA.map((tp) => tp);
    const linhas = [
      [
        'Cliente',
        'Total Despesas',
        'Total Descontos',
        'Investimento Líquido',
        ...tipoHeaders,
        'Qtd Despesas',
        'Total Contratos',
        'Contratos Pagos',
        'Contratos a Pagar',
        'Contratos Vencidos',
        'Dias Atraso (max)',
        'Total Estoque',
        'Qtd Itens Estoque',
        'Budget',
        '% Consumido',
      ].join(sep),
    ];
    data.clientes.forEach((c) => {
      const maxDias =
        c.contratos.vencidos.detalhes.length > 0
          ? Math.max(...c.contratos.vencidos.detalhes.map((v) => v.dias_atraso))
          : 0;
      const tipoVals = TIPOS_DESPESA.map((tp) => ((c.despesas.por_tipo || {})[tp] || 0).toFixed(2));
      const budgetPct = calcBudgetPct(c);
      linhas.push(
        [
          `"${c.cliente_nome}"`,
          c.despesas.total.toFixed(2),
          (c.despesas.total_descontos || 0).toFixed(2),
          ((c.despesas.total || 0) - (c.despesas.total_descontos || 0)).toFixed(2),
          ...tipoVals,
          c.despesas.quantidade,
          c.contratos.total.toFixed(2),
          c.contratos.pagos.total.toFixed(2),
          c.contratos.a_pagar.total.toFixed(2),
          c.contratos.vencidos.total.toFixed(2),
          maxDias,
          c.estoque.total_valor.toFixed(2),
          c.estoque.quantidade_itens,
          (c.budget || 0).toFixed(2),
          budgetPct.toFixed(1) + '%',
        ].join(sep)
      );
    });
    linhas.push('');
    const tipoTotals = TIPOS_DESPESA.map((tp) =>
      ((data.totais.por_tipo || {})[tp] || 0).toFixed(2)
    );
    linhas.push(
      [
        '"TOTAIS"',
        data.totais.total_despesas.toFixed(2),
        (data.totais.total_descontos || 0).toFixed(2),
        ((data.totais.total_despesas || 0) - (data.totais.total_descontos || 0)).toFixed(2),
        ...tipoTotals,
        '',
        data.totais.total_contratos.toFixed(2),
        data.totais.total_contratos_pagos.toFixed(2),
        data.totais.total_contratos_a_pagar.toFixed(2),
        data.totais.total_contratos_vencidos.toFixed(2),
        '',
        data.totais.total_estoque.toFixed(2),
        '',
        (data.totais.total_budget || 0).toFixed(2),
        '',
      ].join(sep)
    );

    const bom = '\uFEFF';
    const blob = new Blob([bom + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resumo_mensal_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Cards de Totais (Planilha tab - compacto)
  const renderTotaisCards = () => {
    if (!data?.totais) return null;
    const t = data.totais;
    const tipoColors = {
      Manutenção: '#7b1fa2',
      Obras: '#0277bd',
      Projetos: '#00695c',
      Reformas: '#e65100',
    };
    const tipoCards = Object.entries(t.por_tipo || {}).map(([tipo, valor]) => ({
      label: tipo || 'Sem tipo',
      value: formatCurrency(valor),
      color: tipoColors[tipo] || '#546e7a',
      icon: <BuildIcon />,
      filterKey: 'tipo',
      filterValue: tipo || '_vazio',
    }));
    const cards = [
      {
        label: 'Total Investimento',
        value: formatCurrency(t.total_despesas),
        color: '#f44336',
        icon: <ReceiptLongIcon />,
        filterKey: null,
      },
      {
        label: 'Total Descontos',
        value: formatCurrency(t.total_descontos || 0),
        color: '#2e7d32',
        icon: <MonetizationOnIcon />,
        filterKey: null,
      },
      {
        label: 'Total Investimento Líquido',
        value: formatCurrency((t.total_despesas || 0) - (t.total_descontos || 0)),
        color: '#1565c0',
        icon: <TrendingUpIcon />,
        filterKey: null,
      },
      ...tipoCards,
      {
        label: 'Total Contratos',
        value: formatCurrency(t.total_contratos),
        color: '#2196f3',
        icon: <AccountBalanceIcon />,
        filterKey: null,
      },
      {
        label: 'Contratos Pagos',
        value: formatCurrency(t.total_contratos_pagos),
        color: '#4caf50',
        icon: <CheckCircleIcon />,
        filterKey: 'situacao',
        filterValue: 'pago',
      },
      {
        label: 'Contratos a Pagar',
        value: formatCurrency(t.total_contratos_a_pagar),
        color: '#ff9800',
        icon: <TrendingUpIcon />,
        filterKey: 'situacao',
        filterValue: 'a_pagar',
      },
      {
        label: 'Contratos Vencidos',
        value: formatCurrency(t.total_contratos_vencidos),
        color: '#d32f2f',
        icon: <WarningAmberIcon />,
        filterKey: 'situacao',
        filterValue: 'vencido',
        extra: t.clientes_com_vencidos > 0 ? `${t.clientes_com_vencidos} cliente(s)` : null,
      },
      {
        label: 'Total Estoque',
        value: formatCurrency(t.total_estoque),
        color: '#795548',
        icon: <InventoryIcon />,
        filterKey: null,
      },
      {
        label: 'Budget Total',
        value: formatCurrency(t.total_budget || 0),
        color: '#1565c0',
        icon: <SavingsIcon />,
        filterKey: null,
      },
    ];
    const handleCardClick = (card) => {
      if (!card.filterKey) return;
      if (card.filterKey === 'tipo') {
        setFiltroTipos((prev) =>
          prev.includes(card.filterValue)
            ? prev.filter((t) => t !== card.filterValue)
            : [...prev, card.filterValue]
        );
      } else if (card.filterKey === 'situacao') {
        setFiltroSituacao((prev) => (prev === card.filterValue ? '' : card.filterValue));
      }
    };
    const isCardActive = (card) => {
      if (!card.filterKey) return false;
      if (card.filterKey === 'tipo') return filtroTipos.includes(card.filterValue);
      if (card.filterKey === 'situacao') return filtroSituacao === card.filterValue;
      return false;
    };
    const mobileResumoCardHeight = 98;
    const renderResumoCard = (c, key) => {
      const active = isCardActive(c);
      const clickable = !!c.filterKey;

      return (
        <Card
          key={key}
          onClick={() => handleCardClick(c)}
          sx={{
            borderLeft: `4px solid ${c.color}`,
            width: '100%',
            height: isMobile ? mobileResumoCardHeight : '100%',
            minHeight: isMobile ? mobileResumoCardHeight : 'auto',
            cursor: clickable ? 'pointer' : 'default',
            transition: 'all 0.2s',
            outline: active ? `2px solid ${c.color}` : 'none',
            boxShadow: active ? `0 0 0 2px ${c.color}33, 0 4px 12px ${c.color}44` : undefined,
            bgcolor: active ? `${c.color}0A` : undefined,
            transform: active && !isMobile ? 'scale(1.03)' : undefined,
            '&:hover':
              clickable && !isMobile
                ? { boxShadow: `0 2px 8px ${c.color}33`, transform: 'translateY(-2px)' }
                : {},
          }}
        >
          <CardContent
            sx={{
              height: '100%',
              py: isMobile ? 1.2 : 1.5,
              px: isMobile ? 1.4 : 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:last-child': { pb: isMobile ? 1.2 : 1.5 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                minHeight: '2.2em',
              }}
            >
              <Box sx={{ color: c.color, flexShrink: 0, mt: 0.1 }}>{c.icon}</Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  flex: 1,
                  lineHeight: 1.1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {c.label}
              </Typography>
            </Box>
            <Box
              sx={{
                minHeight: '2.6em',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: c.color,
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  lineHeight: 1.15,
                }}
              >
                {c.value}
              </Typography>
              <Typography
                variant="caption"
                color="error"
                sx={{
                  minHeight: '1.2em',
                  visibility: c.extra ? 'visible' : 'hidden',
                  lineHeight: 1.1,
                }}
              >
                {c.extra}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    };

    if (isMobile) {
      return (
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: 1.5,
            rowGap: 1.5,
            alignItems: 'stretch',
          }}
        >
          {cards.map((c, i) => (
            <Box key={i} sx={{ minWidth: 0, display: 'flex' }}>
              {renderResumoCard(c, `mobile-${i}`)}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((c, i) => (
          <Grid item xs={6} sm={4} md={2} key={i} sx={{ display: 'flex' }}>
            {renderResumoCard(c, `desktop-${i}`)}
          </Grid>
        ))}
      </Grid>
    );
  };

  // Barra de Filtros (estilo Bluesoft) - unificada para ambas as abas
  const renderFilterBar = () => (
    <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FilterListIcon color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Filtros
        </Typography>
        {hasActiveFilters && (
          <Chip
            label="Limpar filtros"
            size="small"
            icon={<ClearIcon />}
            onClick={handleClearFilters}
            color="default"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Cliente"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
          >
            <MenuItem value="">TODOS GERAL</MenuItem>
            {clientesList.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nome}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Situação"
            value={filtroSituacao}
            onChange={(e) => setFiltroSituacao(e.target.value)}
          >
            <MenuItem value="">TODOS GERAL</MenuItem>
            <MenuItem value="pago">Pago / Concluído</MenuItem>
            <MenuItem value="a_pagar">A Pagar</MenuItem>
            <MenuItem value="vencido">Vencido</MenuItem>
            <MenuItem value="cancelado">Cancelado</MenuItem>
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Fornecedor"
            value={filtroFornecedor}
            onChange={(e) => setFiltroFornecedor(e.target.value)}
          >
            <MenuItem value="">TODOS GERAL</MenuItem>
            {fornecedoresList.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.nome || f.razao_social || `Fornecedor #${f.id}`}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              multiple
              value={filtroTipos}
              onChange={(e) => setFiltroTipos(e.target.value)}
              input={<OutlinedInput label="Tipo" />}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'Todos'
                  : selected.map((v) => (v === '_vazio' ? 'Sem tipo' : v)).join(', ')
              }
            >
              {['_vazio', 'Manutenção', 'Obras', 'Projetos', 'Reformas'].map((t) => (
                <MenuItem key={t} value={t}>
                  <Checkbox checked={filtroTipos.includes(t)} size="small" />
                  <ListItemText primary={t === '_vazio' ? 'Sem tipo' : t} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Data Início"
            value={filtroDataInicio}
            onChange={(e) => setFiltroDataInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Data Fim"
            value={filtroDataFim}
            onChange={(e) => setFiltroDataFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: { xs: '100%', sm: 180 } }}>
          <TextField
            fullWidth
            size="small"
            label="Nº OS"
            value={inputNumeroOS}
            onChange={(e) => setInputNumeroOS(e.target.value)}
            placeholder="Ex: 997615"
          />
        </Box>
      </Box>
    </Paper>
  );

  // KPI Cards estilo Bluesoft (grandes, coloridos, fundo cheio)
  const renderBigKPICards = () => {
    if (!data?.totais) return null;
    const t = data.totais;
    const tipoBgs = {
      Manutenção: 'linear-gradient(135deg, #8e24aa 0%, #6a1b9a 100%)',
      Obras: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
      Projetos: 'linear-gradient(135deg, #00897b 0%, #004d40 100%)',
      Reformas: 'linear-gradient(135deg, #ef6c00 0%, #e65100 100%)',
    };
    const tipoKpis = Object.entries(t.por_tipo || {}).map(([tipo, valor]) => ({
      label: tipo || 'Sem tipo',
      value: valor,
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      bg: tipoBgs[tipo] || 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
      subtitle: 'Tipo de investimento',
      filterKey: 'tipo',
      filterValue: tipo || '_vazio',
    }));
    const kpis = [
      {
        label: 'Total Investimento',
        value: t.total_despesas,
        icon: <ReceiptLongIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #e53935 0%, #d32f2f 100%)',
        subtitle: `${t.quantidade_clientes} clientes`,
        filterKey: null,
      },
      {
        label: 'Total Descontos',
        value: t.total_descontos || 0,
        icon: <MonetizationOnIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
        subtitle: 'Descontos concedidos',
        filterKey: null,
      },
      {
        label: 'Total Investimento Líquido',
        value: (t.total_despesas || 0) - (t.total_descontos || 0),
        icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
        subtitle: 'Investimento − Descontos',
        filterKey: null,
      },
      ...tipoKpis,
      {
        label: 'Total Contratos',
        value: t.total_contratos,
        icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
        subtitle: `Pagos + A Pagar`,
        filterKey: null,
      },
      {
        label: 'Contratos Pagos',
        value: t.total_contratos_pagos,
        icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
        subtitle: 'Concluidos',
        filterKey: 'situacao',
        filterValue: 'pago',
      },
      {
        label: 'A Pagar',
        value: t.total_contratos_a_pagar,
        icon: <PaymentsIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #fb8c00 0%, #ef6c00 100%)',
        subtitle: 'Em aberto',
        filterKey: 'situacao',
        filterValue: 'a_pagar',
      },
      {
        label: 'Vencidos',
        value: t.total_contratos_vencidos,
        icon: <ErrorOutlineIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
        subtitle:
          t.clientes_com_vencidos > 0
            ? `${t.clientes_com_vencidos} cliente(s) em atraso`
            : 'Nenhum',
        filterKey: 'situacao',
        filterValue: 'vencido',
      },
      {
        label: 'Total Estoque',
        value: t.total_estoque,
        icon: <InventoryIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #6d4c41 0%, #4e342e 100%)',
        subtitle: 'Valor em estoque',
        filterKey: null,
      },
      {
        label: 'Budget Total',
        value: t.total_budget || 0,
        icon: <SavingsIcon sx={{ fontSize: 40 }} />,
        bg: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
        subtitle: 'Limite de orçamento',
        filterKey: null,
      },
    ];
    const handleKpiClick = (kpi) => {
      if (!kpi.filterKey) return;
      if (kpi.filterKey === 'tipo') {
        setFiltroTipos((prev) =>
          prev.includes(kpi.filterValue)
            ? prev.filter((t) => t !== kpi.filterValue)
            : [...prev, kpi.filterValue]
        );
      } else if (kpi.filterKey === 'situacao') {
        setFiltroSituacao((prev) => (prev === kpi.filterValue ? '' : kpi.filterValue));
      }
    };
    const isKpiActive = (kpi) => {
      if (!kpi.filterKey) return false;
      if (kpi.filterKey === 'tipo') return filtroTipos.includes(kpi.filterValue);
      if (kpi.filterKey === 'situacao') return filtroSituacao === kpi.filterValue;
      return false;
    };
    const mobileCardHeight = 150;
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((kpi, i) => {
          const active = isKpiActive(kpi);
          const clickable = !!kpi.filterKey;
          return (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Paper
                onClick={() => handleKpiClick(kpi)}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  borderRadius: 2,
                  background: kpi.bg,
                  color: '#fff',
                  height: isMobile ? mobileCardHeight : '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: isMobile ? mobileCardHeight : 130,
                  boxShadow: active
                    ? '0 0 0 3px #fff, 0 0 0 5px rgba(0,0,0,0.4), 0 8px 30px rgba(0,0,0,0.3)'
                    : '0 4px 20px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s',
                  cursor: clickable ? 'pointer' : 'default',
                  transform: active ? 'scale(1.05)' : undefined,
                  '&:hover': clickable
                    ? { transform: 'translateY(-2px)', boxShadow: '0 6px 25px rgba(0,0,0,0.2)' }
                    : {},
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    minHeight: isMobile ? 34 : 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 600,
                      opacity: 0.9,
                      fontSize: isMobile ? '0.68rem' : '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      lineHeight: 1.15,
                      minHeight: isMobile ? '2.3em' : 'auto',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {kpi.label}
                  </Typography>
                  <Box sx={{ opacity: 0.3, flexShrink: 0 }}>{kpi.icon}</Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    minHeight: isMobile ? 52 : 'auto',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      fontSize: isMobile ? '1rem' : '1.3rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {formatCurrency(kpi.value)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      opacity: 0.8,
                      fontSize: '0.7rem',
                      lineHeight: 1.15,
                      minHeight: isMobile ? '2.3em' : 'auto',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {kpi.subtitle}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Tabela Planilha
  const renderTabela = () => {
    if (!data?.clientes) return null;
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1, overflowX: 'hidden' }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& > .MuiTableHead-root th': {
              fontWeight: 700,
              bgcolor: '#f5f5f5',
              whiteSpace: 'nowrap',
              px: 0.75,
              py: 1,
              fontSize: '0.72rem',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '12%' }}>Cliente</TableCell>
              <TableCell align="center" sx={{ width: '7%' }}>
                Total Investimento
              </TableCell>
              <TableCell align="center" sx={{ width: '7%', color: '#2e7d32' }}>
                Descontos
              </TableCell>
              <TableCell align="center" sx={{ width: '7%', color: '#1565c0' }}>
                Inv. Líquido
              </TableCell>
              {tiposVisiveis.map((tp) => (
                <TableCell key={tp} align="center" sx={{ width: '5.5%', color: TIPO_COLORS[tp] }}>
                  {tp}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ width: '7%' }}>
                Total Contratos
              </TableCell>
              <TableCell align="center" sx={{ width: '6%', color: '#4caf50' }}>
                Pagos
              </TableCell>
              <TableCell align="center" sx={{ width: '6%', color: '#ff9800' }}>
                A Pagar
              </TableCell>
              <TableCell align="center" sx={{ width: '7%', color: '#d32f2f' }}>
                Vencidos
              </TableCell>
              <TableCell align="center" sx={{ width: '8%' }}>
                Total Estoque
              </TableCell>
              <TableCell align="center" sx={{ width: '6%', color: '#1565c0' }}>
                Budget
              </TableCell>
              <TableCell align="center" sx={{ width: '8%', color: '#1565c0' }}>
                % Consumido
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.clientes.map((c) => {
              const hasVencidos = c.contratos.vencidos.quantidade > 0;
              const activeCell = expandedCells[c.cliente_id];
              const isExpanded = !!(activeCell?.open);
              const PAGO_SIT = ['pago', 'concluído', 'concluido'];
              const CANCEL_SIT = ['cancelado'];

              const expandBtn = (col, show) =>
                show ? (
                  <IconButton
                    size="small"
                    sx={{ p: 0, ml: 0.3, opacity: 0.55, '&:hover': { opacity: 1 } }}
                    onClick={(e) => { e.stopPropagation(); toggleCell(c.cliente_id, col); }}
                  >
                    {isCellOpen(c.cliente_id, col)
                      ? <ExpandLessIcon sx={{ fontSize: 13 }} />
                      : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
                  </IconButton>
                ) : null;

              const renderContratoRows = (list) =>
                list.map((ct) => {
                  const isVenc = c.contratos.vencidos.detalhes.some((v) => v.id === ct.id);
                  const sit = (ct.situacao || '').toLowerCase();
                  const sitColor = isVenc ? '#d32f2f'
                    : PAGO_SIT.includes(sit) ? '#2e7d32'
                    : CANCEL_SIT.includes(sit) ? '#9e9e9e'
                    : '#e65100';
                  return (
                    <TableRow key={ct.id} sx={{ bgcolor: isVenc ? '#fff3f3' : 'inherit' }}>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{ct.numero}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{formatCurrency(ct.valor)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{ct.data_inicio ? new Date(ct.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{ct.data_fim ? new Date(ct.data_fim + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                      <TableCell>
                        <Chip label={ct.situacao} size="small" sx={{ fontSize: '0.7rem', bgcolor: sitColor + '22', color: sitColor, borderColor: sitColor, border: '1px solid' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{ct.tipo}</TableCell>
                    </TableRow>
                  );
                });

              let detailContent = null;
              if (isExpanded) {
                const col = activeCell.col;
                const despDet = c.despesas?.detalhes || [];
                const ctDet = c.contratos?.detalhes || [];

                if (col === 'total' || TIPOS_DESPESA.includes(col) || col === 'Sem tipo' || col === 'descontos') {
                  let filtrado = despDet;
                  let title = 'Todas as Despesas';
                  let borderColor = '#555';
                  if (col === 'descontos') {
                    filtrado = despDet.filter((d) => (d.desconto || 0) > 0);
                    title = 'Despesas com Desconto';
                    borderColor = '#2e7d32';
                  } else if (col !== 'total') {
                    filtrado = despDet.filter((d) => (d.tipo || 'Sem tipo') === col);
                    title = `Despesas — ${col}`;
                    borderColor = TIPO_COLORS[col] || '#555';
                  }
                  detailContent = (
                    <Box sx={{ bgcolor: borderColor + '11', borderLeft: `4px solid ${borderColor}`, borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: borderColor }}>
                        {title} ({filtrado.length})
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>Serviço</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>N° OS</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell align="right" sx={{ color: '#2e7d32' }}>Desconto</TableCell>
                            <TableCell>Data</TableCell>
                            {col === 'total' && <TableCell>Tipo</TableCell>}
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtrado.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{d.servico}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.descricao || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{d.numero_os || '-'}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{formatCurrency(d.valor)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#2e7d32' }}>
                                {(d.desconto || 0) > 0 ? formatCurrency(d.desconto) : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{d.data ? new Date(d.data + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                              {col === 'total' && <TableCell><Chip label={d.tipo} size="small" sx={{ fontSize: '0.68rem' }} /></TableCell>}
                              <TableCell sx={{ fontSize: '0.78rem' }}>{d.status || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                } else if (col === 'contratos') {
                  detailContent = (
                    <Box sx={{ bgcolor: '#f3f6fb', borderLeft: '4px solid #1976d2', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1976d2' }}>
                        Todos os Contratos ({ctDet.length})
                        {hasVencidos && <Chip label={`${c.contratos.vencidos.quantidade} vencido(s)`} size="small" color="error" sx={{ ml: 1 }} />}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>N° Contrato</TableCell><TableCell align="right">Valor</TableCell>
                            <TableCell>Início</TableCell><TableCell>Fim</TableCell>
                            <TableCell>Situação</TableCell><TableCell>Tipo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>{renderContratoRows(ctDet)}</TableBody>
                      </Table>
                    </Box>
                  );
                } else if (col === 'pagos') {
                  const list = ctDet.filter((ct) => PAGO_SIT.includes((ct.situacao || '').toLowerCase()));
                  detailContent = (
                    <Box sx={{ bgcolor: '#f1fff5', borderLeft: '4px solid #2e7d32', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e7d32' }}>Contratos Pagos ({list.length})</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>N° Contrato</TableCell><TableCell align="right">Valor</TableCell>
                            <TableCell>Início</TableCell><TableCell>Fim</TableCell>
                            <TableCell>Situação</TableCell><TableCell>Tipo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>{renderContratoRows(list)}</TableBody>
                      </Table>
                    </Box>
                  );
                } else if (col === 'a_pagar') {
                  const list = ctDet.filter((ct) => {
                    const s = (ct.situacao || '').toLowerCase();
                    return !PAGO_SIT.includes(s) && !CANCEL_SIT.includes(s);
                  });
                  detailContent = (
                    <Box sx={{ bgcolor: '#fff8f0', borderLeft: '4px solid #e65100', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#e65100' }}>Contratos a Pagar ({list.length})</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>N° Contrato</TableCell><TableCell align="right">Valor</TableCell>
                            <TableCell>Início</TableCell><TableCell>Fim</TableCell>
                            <TableCell>Situação</TableCell><TableCell>Tipo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>{renderContratoRows(list)}</TableBody>
                      </Table>
                    </Box>
                  );
                } else if (col === 'vencidos') {
                  const list = c.contratos.vencidos.detalhes || [];
                  detailContent = (
                    <Box sx={{ bgcolor: '#fef3f2', borderLeft: '4px solid #d32f2f', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Contratos Vencidos ({list.length})</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>N° Contrato</TableCell><TableCell align="right">Valor</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell align="center" sx={{ color: '#d32f2f' }}>Dias em Atraso</TableCell>
                            <TableCell>Situação</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {list.map((v) => (
                            <TableRow key={v.id} sx={{ bgcolor: '#fff3f3' }}>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{v.numero}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{formatCurrency(v.valor)}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{v.data_fim ? new Date(v.data_fim + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                              <TableCell align="center">
                                <Chip label={`${v.dias_atraso} dias`} size="small" color={v.dias_atraso > 30 ? 'error' : 'warning'} variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{v.motivo}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                } else if (col === 'os') {
                  const list = c.os?.detalhes || [];
                  detailContent = (
                    <Box sx={{ bgcolor: '#f3fbf6', borderLeft: '4px solid #388e3c', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#388e3c' }}>OS / NFE ({list.length})</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', py: 0.5 } }}>
                            <TableCell>N° OS</TableCell><TableCell>Descrição</TableCell>
                            <TableCell>Status</TableCell><TableCell>N° NFE</TableCell>
                            <TableCell>Data Recebimento</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {list.map((os) => {
                            const stColor = os.status === 'Recebido' ? '#2e7d32' : os.status === 'Em andamento' ? '#1565c0' : '#e65100';
                            return (
                              <TableRow key={os.id}>
                                <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{os.numero_os}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{os.descricao || '-'}</TableCell>
                                <TableCell><Chip label={os.status} size="small" sx={{ fontSize: '0.7rem', bgcolor: stColor + '22', color: stColor, borderColor: stColor, border: '1px solid' }} /></TableCell>
                                <TableCell sx={{ fontSize: '0.78rem' }}>{os.numero_nfe || '-'}</TableCell>
                                <TableCell sx={{ fontSize: '0.78rem' }}>{os.data_recebimento ? new Date(os.data_recebimento).toLocaleDateString('pt-BR') : '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                }
              }

              return (
                <React.Fragment key={c.cliente_id}>
                  <TableRow hover sx={{ bgcolor: hasVencidos ? '#fff8e1' : 'inherit' }}>
                    <TableCell sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.cliente_nome}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {expandBtn('total', (c.despesas.total || 0) > 0)}
                        {(c.despesas.total || 0) > 0 ? formatCurrency(c.despesas.total) : '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#2e7d32', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {expandBtn('descontos', (c.despesas.total_descontos || 0) > 0)}
                        {(c.despesas.total_descontos || 0) > 0 ? formatCurrency(c.despesas.total_descontos) : '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#1565c0', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {formatCurrency((c.despesas.total || 0) - (c.despesas.total_descontos || 0))}
                    </TableCell>
                    {tiposVisiveis.map((tp) => {
                      const val = (c.despesas.por_tipo || {})[tp] || 0;
                      return (
                        <TableCell key={tp} align="right" sx={{ color: TIPO_COLORS[tp], whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {expandBtn(tp, val > 0)}
                            {val > 0 ? formatCurrency(val) : '-'}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {expandBtn('contratos', c.contratos.total > 0)}
                        {c.contratos.total > 0 ? formatCurrency(c.contratos.total) : '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4caf50', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {expandBtn('pagos', c.contratos.pagos.total > 0)}
                        {c.contratos.pagos.total > 0 ? formatCurrency(c.contratos.pagos.total) : '-'}
                      </Box>
                      <Typography variant="caption" display="block" color="text.secondary">({c.contratos.pagos.quantidade})</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#ff9800', whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {expandBtn('a_pagar', c.contratos.a_pagar.total > 0)}
                        {c.contratos.a_pagar.total > 0 ? formatCurrency(c.contratos.a_pagar.total) : '-'}
                      </Box>
                      <Typography variant="caption" display="block" color="text.secondary">({c.contratos.a_pagar.quantidade})</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#d32f2f' }}>
                      {hasVencidos ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {expandBtn('vencidos', true)}
                            <WarningAmberIcon fontSize="small" color="error" />
                            {formatCurrency(c.contratos.vencidos.total)}
                          </Box>
                          <Typography variant="caption" display="block" color="error">({c.contratos.vencidos.quantidade}) em atraso</Typography>
                        </Box>
                      ) : <Typography variant="body2" color="text.secondary">-</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {c.estoque.total_valor > 0 ? formatCurrency(c.estoque.total_valor) : '-'}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.estoque.quantidade_itens} itens / {c.estoque.quantidade_pecas} pcs
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#1565c0', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {(c.budget || 0) > 0 ? formatCurrency(c.budget) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {(c.budget || 0) > 0 ? (
                        (() => {
                          const pct = calcBudgetPct(c);
                          const color = getBudgetColor(pct);
                          return (
                            <Tooltip title={`Consumido: ${formatCurrency(calcBudgetConsumed(c))} de ${formatCurrency(c.budget)}`}>
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, color }}>{pct.toFixed(1)}%</Typography>
                                <LinearProgress variant="determinate" value={clampBudgetPct(pct)}
                                  sx={{ height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
                                />
                              </Box>
                            </Tooltip>
                          );
                        })()
                      ) : <Typography variant="body2" color="text.secondary">-</Typography>}
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={11 + tiposVisiveis.length} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2 }}>{detailContent}</Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {/* Linha de totais */}
            <TableRow
              sx={{
                '& td': { fontWeight: 700, bgcolor: '#e3f2fd', borderTop: '2px solid #1976d2' },
              }}
            >
              <TableCell>TOTAIS</TableCell>
              <TableCell align="right">{formatCurrency(data.totais.total_despesas)}</TableCell>
              <TableCell align="right" sx={{ color: '#2e7d32' }}>
                {formatCurrency(data.totais.total_descontos || 0)}
              </TableCell>
              <TableCell align="right" sx={{ color: '#1565c0' }}>
                {formatCurrency((data.totais.total_despesas || 0) - (data.totais.total_descontos || 0))}
              </TableCell>
              {tiposVisiveis.map((tp) => (
                <TableCell key={tp} align="right" sx={{ color: TIPO_COLORS[tp] }}>
                  {formatCurrency((data.totais.por_tipo || {})[tp] || 0)}
                </TableCell>
              ))}
              <TableCell align="right">{formatCurrency(data.totais.total_contratos)}</TableCell>
              <TableCell align="right" sx={{ color: '#4caf50' }}>
                {formatCurrency(data.totais.total_contratos_pagos)}
              </TableCell>
              <TableCell align="right" sx={{ color: '#ff9800' }}>
                {formatCurrency(data.totais.total_contratos_a_pagar)}
              </TableCell>
              <TableCell align="right" sx={{ color: '#d32f2f' }}>
                {formatCurrency(data.totais.total_contratos_vencidos)}
              </TableCell>
              <TableCell align="right">{formatCurrency(data.totais.total_estoque)}</TableCell>
              <TableCell align="right" sx={{ color: '#1565c0' }}>
                {formatCurrency(data.totais.total_budget || 0)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Tabela Mobile (Cards)
  const renderTabelaMobile = () => {
    if (!data?.clientes) return null;
    return (
      <Box>
        {data.clientes.map((c) => {
          const hasVencidos = c.contratos.vencidos.quantidade > 0;
          return (
            <Card
              key={c.cliente_id}
              sx={{
                mb: 2,
                borderRadius: 2,
                borderLeft: hasVencidos ? '4px solid #d32f2f' : '4px solid #2196f3',
              }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {c.cliente_nome}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Investimento
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                      {formatCurrency(c.despesas.total)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Descontos
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {(c.despesas.total_descontos || 0) > 0
                        ? formatCurrency(c.despesas.total_descontos)
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Contratos
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(c.contratos.total)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Pagos
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4caf50' }}>
                      {formatCurrency(c.contratos.pagos.total)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      A Pagar
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ff9800' }}>
                      {formatCurrency(c.contratos.a_pagar.total)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Vencidos
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#d32f2f', fontWeight: hasVencidos ? 700 : 400 }}
                    >
                      {hasVencidos ? formatCurrency(c.contratos.vencidos.total) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Estoque
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(c.estoque.total_valor)}
                    </Typography>
                  </Grid>
                  {(c.budget || 0) > 0 && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Budget
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>
                        {formatCurrency(c.budget)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={clampBudgetPct(calcBudgetPct(c))}
                          sx={{
                            flex: 1,
                            height: 5,
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getBudgetColor(calcBudgetPct(c)),
                              borderRadius: 3,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: getBudgetColor(calcBudgetPct(c)) }}
                        >
                          {calcBudgetPct(c).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {hasVencidos && (
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" color="error" onClick={() => toggleRow(c.cliente_id)}>
                      {expandedRows[c.cliente_id]
                        ? 'Ocultar vencidos'
                        : `Ver ${c.contratos.vencidos.quantidade} vencido(s)`}
                    </Button>
                    <Collapse in={expandedRows[c.cliente_id]}>
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#fef3f2', borderRadius: 1 }}>
                        {c.contratos.vencidos.detalhes.map((v) => (
                          <Box key={v.id} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #eee' }}>
                            <Typography variant="caption">
                              <strong>N:</strong> {v.numero} | <strong>Valor:</strong>{' '}
                              {formatCurrency(v.valor)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Venc.:</strong>{' '}
                              {v.data_fim
                                ? new Date(v.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')
                                : '-'}
                              {' | '}
                              <Chip
                                label={`${v.dias_atraso} dias`}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Motivo:</strong> {v.motivo}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // Dashboard BI (estilo Bluesoft Intelligence)
  const renderDashboard = () => {
    if (!data?.clientes) return null;
    const clientes = data.clientes.filter(
      (c) => c.despesas.total > 0 || c.contratos.total > 0 || c.estoque.total_valor > 0
    );

    // Dados para grafico horizontal "Por Cliente" - Despesas vs Contratos
    const clienteBarData = [...clientes]
      .sort((a, b) => b.despesas.total + b.contratos.total - (a.despesas.total + a.contratos.total))
      .slice(0, 10)
      .map((c) => ({
        nome: c.cliente_nome.length > 20 ? c.cliente_nome.substring(0, 20) + '...' : c.cliente_nome,
        nomeCompleto: c.cliente_nome,
        Despesas: c.despesas.total,
        Contratos: c.contratos.total,
        Pagos: c.contratos.pagos.total,
        'A Pagar': c.contratos.a_pagar.total,
        Vencidos: c.contratos.vencidos.total,
      }));

    // Dados para grafico "Por Fornecedor"
    const fornecedorBarData = (data.fornecedores_breakdown || []).slice(0, 10).map((f) => ({
      nome:
        (f.fornecedor_nome || 'Sem fornecedor').length > 20
          ? (f.fornecedor_nome || 'Sem fornecedor').substring(0, 20) + '...'
          : f.fornecedor_nome || 'Sem fornecedor',
      nomeCompleto: f.fornecedor_nome || 'Sem fornecedor',
      'Total Despesas': f.total,
      quantidade: f.quantidade,
    }));

    // Pie distribuicao
    const tipoSlices = TIPOS_DESPESA.map((tp) => ({
      name: tp,
      value: (data.totais.por_tipo || {})[tp] || 0,
    }));
    const pieDistribuicao = [
      { name: 'Despesas', value: data.totais.total_despesas },
      { name: 'Descontos', value: data.totais.total_descontos || 0 },
      ...tipoSlices,
      { name: 'Contratos Pagos', value: data.totais.total_contratos_pagos },
      { name: 'A Pagar', value: data.totais.total_contratos_a_pagar },
      { name: 'Vencidos', value: data.totais.total_contratos_vencidos },
      { name: 'Estoque', value: data.totais.total_estoque },
    ].filter((d) => d.value > 0);
    const pieColors = [
      '#e53935',
      '#2e7d32',
      ...TIPOS_DESPESA.map((tp) => TIPO_COLORS[tp]),
      '#43a047',
      '#fb8c00',
      '#c62828',
      '#6d4c41',
    ];

    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <Paper sx={{ p: 1.5, maxWidth: 300 }} elevation={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {payload[0]?.payload?.nomeCompleto || label}
            </Typography>
            {payload.map((p, i) => (
              <Typography key={i} variant="body2" sx={{ color: p.color }}>
                {p.name}: {formatCurrency(p.value)}
              </Typography>
            ))}
          </Paper>
        );
      }
      return null;
    };

    return (
      <Box>
        {/* KPI Cards grandes coloridos */}
        {renderBigKPICards()}

        <Grid container spacing={3}>
          {/* Grafico colunas verticais - Por Cliente */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100%',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': { boxShadow: 4 },
              }}
              onClick={() => setExpandedChart('cliente')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StorefrontIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Por Cliente
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Top {clienteBarData.length}
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <FullscreenIcon />
                </IconButton>
              </Box>
              {clienteBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={clienteBarData}
                    margin={{ left: 0, right: 10, top: 10, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      type="category"
                      dataKey="nome"
                      fontSize={10}
                      tick={{ fill: '#333' }}
                      angle={-40}
                      textAnchor="end"
                      interval={0}
                      height={70}
                    />
                    <YAxis
                      type="number"
                      tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`)}
                      fontSize={11}
                    />
                    <RTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '10px' }} />
                    <Bar dataKey="Despesas" fill="#e53935" radius={[3, 3, 0, 0]} maxBarSize={30} />
                    <Bar dataKey="Contratos" fill="#1e88e5" radius={[3, 3, 0, 0]} maxBarSize={30} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  Sem dados
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Grafico colunas verticais - Por Fornecedor */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100%',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': { boxShadow: 4 },
              }}
              onClick={() => setExpandedChart('fornecedor')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MonetizationOnIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Por Fornecedor
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Top {fornecedorBarData.length}
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  <FullscreenIcon />
                </IconButton>
              </Box>
              {fornecedorBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={fornecedorBarData}
                    margin={{ left: 0, right: 10, top: 10, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      type="category"
                      dataKey="nome"
                      fontSize={10}
                      tick={{ fill: '#333' }}
                      angle={-40}
                      textAnchor="end"
                      interval={0}
                      height={70}
                    />
                    <YAxis
                      type="number"
                      tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`)}
                      fontSize={11}
                    />
                    <RTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '10px' }} />
                    <Bar
                      dataKey="Total Despesas"
                      fill="#7b1fa2"
                      radius={[3, 3, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  Sem dados de fornecedores
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Pie - Distribuicao Geral */}
          <Grid item xs={12} md={5}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100%',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': { boxShadow: 4 },
              }}
              onClick={() => setExpandedChart('distribuicao')}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Distribuicao Geral
                </Typography>
                <IconButton size="small">
                  <FullscreenIcon />
                </IconButton>
              </Box>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieDistribuicao}
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={50}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine
                    paddingAngle={2}
                  >
                    {pieDistribuicao.map((_, i) => (
                      <Cell key={i} fill={pieColors[i % pieColors.length]} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Detalhes composicao dos valores */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Detalhamento por Cliente
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5', fontSize: '0.75rem' } }}
                    >
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">Investimento</TableCell>
                      <TableCell align="right">Pagos</TableCell>
                      <TableCell align="right">A Pagar</TableCell>
                      <TableCell align="right">Vencidos</TableCell>
                      <TableCell align="right">Estoque</TableCell>
                      <TableCell align="right">Budget</TableCell>
                      <TableCell align="center">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientes.map((c) => (
                      <TableRow
                        key={c.cliente_id}
                        hover
                        sx={{ '& td': { fontSize: '0.8rem', py: 0.8 } }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.cliente_nome}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#e53935' }}>
                          {formatCurrency(c.despesas.total)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#43a047' }}>
                          {formatCurrency(c.contratos.pagos.total)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#fb8c00' }}>
                          {formatCurrency(c.contratos.a_pagar.total)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: c.contratos.vencidos.total > 0 ? '#c62828' : 'text.secondary',
                            fontWeight: c.contratos.vencidos.total > 0 ? 700 : 400,
                          }}
                        >
                          {c.contratos.vencidos.total > 0
                            ? formatCurrency(c.contratos.vencidos.total)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(c.estoque.total_valor)}</TableCell>
                        <TableCell align="right" sx={{ color: '#1565c0' }}>
                          {(c.budget || 0) > 0 ? formatCurrency(c.budget) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {(c.budget || 0) > 0
                            ? (() => {
                                const pct = calcBudgetPct(c);
                                return (
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: getBudgetColor(pct) }}
                                  >
                                    {pct.toFixed(0)}%
                                  </Typography>
                                );
                              })()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        '& td': {
                          fontWeight: 700,
                          borderTop: '2px solid #1565c0',
                          bgcolor: '#e3f2fd',
                          fontSize: '0.8rem',
                        },
                      }}
                    >
                      <TableCell>TOTAL</TableCell>
                      <TableCell align="right" sx={{ color: '#e53935' }}>
                        {formatCurrency(data.totais.total_despesas)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#43a047' }}>
                        {formatCurrency(data.totais.total_contratos_pagos)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fb8c00' }}>
                        {formatCurrency(data.totais.total_contratos_a_pagar)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#c62828' }}>
                        {formatCurrency(data.totais.total_contratos_vencidos)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(data.totais.total_estoque)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#1565c0' }}>
                        {formatCurrency(data.totais.total_budget || 0)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Alertas de Contratos Vencidos */}
          {data.clientes.some((c) => c.contratos.vencidos.quantidade > 0) && (
            <Grid item xs={12}>
              <Paper
                sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffcc80' }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#e65100',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <WarningAmberIcon /> Alertas - Contratos Vencidos
                </Typography>
                <Grid container spacing={2}>
                  {data.clientes
                    .filter((c) => c.contratos.vencidos.quantidade > 0)
                    .map((c) => (
                      <Grid item xs={12} md={6} key={c.cliente_id}>
                        <Card sx={{ border: '1px solid #ef9a9a', borderRadius: 2 }}>
                          <CardContent sx={{ pb: '12px !important' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                              {c.cliente_nome}
                            </Typography>
                            {c.contratos.vencidos.detalhes.map((v) => (
                              <Box
                                key={v.id}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mt: 1,
                                  p: 1,
                                  bgcolor: '#fef3f2',
                                  borderRadius: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="body2">
                                    <strong>Contrato {v.numero}</strong> — {formatCurrency(v.valor)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Venc.:{' '}
                                    {v.data_fim
                                      ? new Date(v.data_fim + 'T00:00:00').toLocaleDateString(
                                          'pt-BR'
                                        )
                                      : '-'}{' '}
                                    | {v.motivo}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={`${v.dias_atraso} dias`}
                                  color={
                                    v.dias_atraso > 60
                                      ? 'error'
                                      : v.dias_atraso > 30
                                        ? 'warning'
                                        : 'default'
                                  }
                                  size="small"
                                />
                              </Box>
                            ))}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Modal de Gráfico Expandido */}
        <Dialog
          open={!!expandedChart}
          onClose={() => setExpandedChart(null)}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {expandedChart === 'cliente' && 'Por Cliente - Detalhado'}
            {expandedChart === 'fornecedor' && 'Por Fornecedor - Detalhado'}
            {expandedChart === 'distribuicao' && 'Distribuição Geral - Detalhado'}
            <IconButton onClick={() => setExpandedChart(null)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ height: '80vh', p: 3, overflow: 'hidden' }}>
            {expandedChart === 'cliente' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clienteBarData}
                  margin={{ left: 0, right: 10, top: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    type="category"
                    dataKey="nome"
                    fontSize={12}
                    tick={{ fill: '#333' }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={100}
                  />
                  <YAxis
                    type="number"
                    tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`)}
                    fontSize={12}
                  />
                  <RTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '20px' }} />
                  <Bar dataKey="Despesas" fill="#e53935" radius={[4, 4, 0, 0]} maxBarSize={50} />
                  <Bar dataKey="Contratos" fill="#1e88e5" radius={[4, 4, 0, 0]} maxBarSize={50} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {expandedChart === 'fornecedor' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={fornecedorBarData}
                  margin={{ left: 0, right: 10, top: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    type="category"
                    dataKey="nome"
                    fontSize={12}
                    tick={{ fill: '#333' }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={100}
                  />
                  <YAxis
                    type="number"
                    tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`)}
                    fontSize={12}
                  />
                  <RTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '20px' }} />
                  <Bar
                    dataKey="Total Despesas"
                    fill="#7b1fa2"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
            {expandedChart === 'distribuicao' && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieDistribuicao}
                    cx="50%"
                    cy="50%"
                    outerRadius={250}
                    innerRadius={120}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine
                    paddingAngle={2}
                  >
                    {pieDistribuicao.map((_, i) => (
                      <Cell key={i} fill={pieColors[i % pieColors.length]} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(value) => formatCurrency(value)} />
                  <Legend wrapperStyle={{ fontSize: '18px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  };

  return (
    <Box
      sx={{ background: theme.palette.background.default, minHeight: '100vh', p: { xs: 1, md: 3 } }}
    >
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
            Resumo Mensal
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDashboard}
            disabled={loading}
            size="small"
          >
            Atualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={!data}
            size="small"
          >
            Exportar CSV
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && data && (
          <>
            {renderTotaisCards()}

            {/* Barra de Filtros (Global para Planilha e Dashboard) */}
            {renderFilterBar()}

            <Paper sx={{ mb: 2, borderRadius: 2 }}>
              <Tabs
                value={tabIndex}
                onChange={(_, v) => setTabIndex(v)}
                indicatorColor="primary"
                textColor="primary"
                sx={{ px: 2 }}
              >
                <Tab label="Planilha" />
                <Tab label="Dashboard BI" />
              </Tabs>
            </Paper>

            {tabIndex === 0 && <>{isMobile ? renderTabelaMobile() : renderTabela()}</>}
            {tabIndex === 1 && renderDashboard()}
          </>
        )}
      </Box>
    </Box>
  );
}
